import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

// 3rd party libraries
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

// Our Own imports
import { Privilege, useCan } from '@feedflo/access-control';
import { CalendarIcon } from '../../atoms/Icons';
import ComboboxMulti from '../../atoms/ComboboxMulti';
import Heading from '../../atoms/Heading';
import Page from '../../atoms/Page';
import FeedFloDatePicker from '../../molecules/FeedFloDatePicker';
import FeedFloLabChart from '../../molecules/FeedFloLabsChart';
import FeedFloTable from '../../organisms/FeedFloTable';
import { CRASH_REPORTS_MONITOR_GQL, CRASH_REPORTS_ORG_FILTER_OPTIONS_GQL } from './queries';

// Relative path imports
import './CrashReportsMonitorPage.scss';

const CRASH_REPORT_CODE = 3016;
const DEFAULT_FIRMWARE_BRANCH_ID = '';
const FEEDFLO_ORG_NAME = 'FeedFlo';
const HOUR_SECONDS = 60 * 60;
const MAX_STACKED_SERIES = 8;
const TOP_BARNS_COUNT = 5;
const TOP_ORGS_COUNT = 5;
const EXCLUDED_ORG_NAME_PATTERNS = ['feedflo', 'mock', 'demo', 'test', 'empty'];
const IMPACT_AXIS_LABEL_MAX = 16;

function safeDecodeCrashReport(details) {
  if (!details) {
    return 'Unknown crash report';
  }
  try {
    return atob(details);
  } catch (err) {
    return details;
  }
}

function truncateAxisLabel(label, maxLength = IMPACT_AXIS_LABEL_MAX) {
  if (!label) return 'Unknown';
  if (label.length <= maxLength) return label;
  return `${label.slice(0, maxLength - 1)}…`;
}

function isExcludedOrgName(name = '') {
  const normalized = name.toLowerCase();
  return EXCLUDED_ORG_NAME_PATTERNS.some((pattern) => normalized.includes(pattern));
}

function CrashReportsMonitorPage() {
  // --- State hooks ---
  const [dateRange, setDateRange] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [firmwareBranchId, setFirmwareBranchId] = useState(DEFAULT_FIRMWARE_BRANCH_ID);
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [orgFilterInitialized, setOrgFilterInitialized] = useState(false);

  // --- Atoms and other Hooks ---
  const { canAnywhere, loading: privilegesLoading } = useCan();
  const normalizedFirmwareBranchId = firmwareBranchId.trim();
  const { data: orgFilterData } = useQuery(CRASH_REPORTS_ORG_FILTER_OPTIONS_GQL, {
    skip: privilegesLoading,
    context: { headers: { 'x-hasura-role': 'user' } },
  });
  const orgOptions = useMemo(() => {
    return (orgFilterData?.organization || [])
      .filter((org) => !isExcludedOrgName(org.name))
      .map((org) => ({ id: org.id, name: org.name }));
  }, [orgFilterData]);

  useEffect(() => {
    if (!orgFilterInitialized && orgOptions.length > 0) {
      setSelectedOrgIds(orgOptions.map((org) => org.id));
      setOrgFilterInitialized(true);
    }
  }, [orgFilterInitialized, orgOptions]);

  useEffect(() => {
    const validOrgIdSet = new Set(orgOptions.map((org) => org.id));
    setSelectedOrgIds((prev) => prev.filter((id) => validOrgIdSet.has(id)));
  }, [orgOptions]);

  const deviceWhere = useMemo(() => {
    const where = {
      device_assignments: {
        ended_at: { _is_null: true },
        deleted_at: { _is_null: true },
        feed_line: {
          deleted_at: { _is_null: true },
          farm: {
            deleted_at: { _is_null: true },
            organization_id: { _in: selectedOrgIds },
          },
        },
      },
    };
    if (normalizedFirmwareBranchId) {
      where.firmware_branch_id = { _eq: normalizedFirmwareBranchId };
    }
    return where;
  }, [normalizedFirmwareBranchId, selectedOrgIds]);

  // --- Apollo/GraphQL hooks ---
  const { data, loading, error } = useQuery(CRASH_REPORTS_MONITOR_GQL, {
    variables: {
      from: dateRange?.from?.unix() || 0,
      to: dateRange?.to?.unix() || 0,
      faultCode: CRASH_REPORT_CODE,
      deviceWhere,
    },
    skip: !dateRange || privilegesLoading || !orgFilterInitialized,
    fetchPolicy: 'network-only',
    context: { headers: { 'x-hasura-role': 'user' } },
  });

  // --- Navigation/side effects ---
  useEffect(() => {
    setDateRange({
      from: dayjs.tz().subtract(24, 'hour'),
      to: dayjs.tz(),
    });
  }, []);

  // --- Derived state ---
  const hasStaffAccess = canAnywhere('feature', Privilege.FEATURE_STAFF_ACCESS);

  const chartData = useMemo(() => {
    if (!data?.fault || !dateRange) {
      return [];
    }

    const fromUnix = dateRange.from.unix();
    const toUnix = dateRange.to.unix();
    const bucketCount = Math.max(1, Math.ceil((toUnix - fromUnix) / HOUR_SECONDS));
    const x = [];

    for (let i = 0; i < bucketCount; i += 1) {
      x.push(dayjs.tz(1000 * (fromUnix + i * HOUR_SECONDS)).format('MMM D HH:mm'));
    }

    const totalsByReport = {};
    data.fault.forEach((fault) => {
      const report = safeDecodeCrashReport(fault.details);
      totalsByReport[report] = (totalsByReport[report] || 0) + 1;
    });

    const sortedReportNames = Object.entries(totalsByReport)
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
    const visibleReportNames = sortedReportNames.slice(0, MAX_STACKED_SERIES);
    const hiddenReportNames = new Set(sortedReportNames.slice(MAX_STACKED_SERIES));

    const yByReport = {};
    visibleReportNames.forEach((name) => {
      yByReport[name] = new Array(bucketCount).fill(0);
    });
    if (hiddenReportNames.size > 0) {
      yByReport.Other = new Array(bucketCount).fill(0);
    }

    data.fault.forEach((fault) => {
      const bucketIndex = Math.floor((fault.started_at - fromUnix) / HOUR_SECONDS);
      if (bucketIndex >= 0 && bucketIndex < bucketCount) {
        const report = safeDecodeCrashReport(fault.details);
        const seriesName = hiddenReportNames.has(report) ? 'Other' : report;
        if (yByReport[seriesName]) {
          yByReport[seriesName][bucketIndex] += 1;
        }
      }
    });

    return Object.entries(yByReport).map(([seriesName, y]) => ({
      type: 'bar',
      name: seriesName,
      x,
      y: y.map((count) => (count === 0 ? null : count)),
      hovertemplate: '<b>%{fullData.name}</b><br>Count: %{y}<extra></extra>',
    }));
  }, [data, dateRange]);

  const crashReportRows = useMemo(() => {
    if (!data?.fault) {
      return [];
    }

    const countsByReport = {};
    data.fault.forEach((fault) => {
      const key = safeDecodeCrashReport(fault.details);
      if (!countsByReport[key]) {
        countsByReport[key] = {
          occurrences: 0,
          serials: new Set(),
          latestFaultId: fault?.id || null,
          latestFaultStartedAt: fault?.started_at || 0,
        };
      }
      countsByReport[key].occurrences += 1;
      if (fault?.device?.serial) {
        countsByReport[key].serials.add(fault.device.serial);
      }
      if ((fault?.started_at || 0) > countsByReport[key].latestFaultStartedAt) {
        countsByReport[key].latestFaultStartedAt = fault.started_at;
        countsByReport[key].latestFaultId = fault.id;
      }
    });

    return Object.entries(countsByReport)
      .sort((a, b) => b[1].occurrences - a[1].occurrences)
      .map(([crashReport, reportData]) => {
        const serials = Array.from(reportData.serials).sort((a, b) => a.localeCompare(b));
        const params = new URLSearchParams();
        params.set('name', 'Crash Report Affected Devices');
        serials.forEach((serial) => params.append('d', serial));

        return [
          crashReport,
          reportData.occurrences,
          serials.length > 0 ? (
            <Link to={`/devices?${params.toString()}`}>{serials.length} device(s)</Link>
          ) : (
            '—'
          ),
          reportData.latestFaultId ? (
            <Link to={`/fault/${reportData.latestFaultId}`}>
              {dayjs.tz(1000 * reportData.latestFaultStartedAt).fromNow()}
            </Link>
          ) : (
            '—'
          ),
        ];
      });
  }, [data]);

  const topBarnsChartData = useMemo(() => {
    if (!data?.fault) {
      return [];
    }

    const countsByBarn = {};
    const crashTotals = {};
    data.fault.forEach((fault) => {
      const barnName = fault?.device?.device_assignments?.[0]?.feed_line?.farm?.name || 'Unknown barn';
      const crashName = safeDecodeCrashReport(fault.details);
      countsByBarn[barnName] = (countsByBarn[barnName] || 0) + 1;
      crashTotals[crashName] = (crashTotals[crashName] || 0) + 1;
    });

    const topBarns = Object.entries(countsByBarn)
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_BARNS_COUNT)
      .map(([barnName]) => barnName);

    if (topBarns.length === 0) {
      return [];
    }

    const sortedCrashNames = Object.entries(crashTotals)
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
    const visibleCrashNames = sortedCrashNames.slice(0, MAX_STACKED_SERIES);
    const hiddenCrashNames = new Set(sortedCrashNames.slice(MAX_STACKED_SERIES));

    const countsByBarnAndCrash = {};
    topBarns.forEach((barnName) => {
      countsByBarnAndCrash[barnName] = {};
      visibleCrashNames.forEach((crashName) => {
        countsByBarnAndCrash[barnName][crashName] = 0;
      });
      if (hiddenCrashNames.size > 0) {
        countsByBarnAndCrash[barnName].Other = 0;
      }
    });

    data.fault.forEach((fault) => {
      const barnName = fault?.device?.device_assignments?.[0]?.feed_line?.farm?.name || 'Unknown barn';
      if (!countsByBarnAndCrash[barnName]) {
        return;
      }
      const crashName = safeDecodeCrashReport(fault.details);
      const seriesName = hiddenCrashNames.has(crashName) ? 'Other' : crashName;
      if (typeof countsByBarnAndCrash[barnName][seriesName] === 'number') {
        countsByBarnAndCrash[barnName][seriesName] += 1;
      }
    });

    const barnOrder = [...topBarns].reverse();
    const barnDisplayOrder = barnOrder.map((barnName) => truncateAxisLabel(barnName));
    const seriesNames = hiddenCrashNames.size > 0 ? [...visibleCrashNames, 'Other'] : [...visibleCrashNames];

    return seriesNames.map((seriesName) => ({
      type: 'bar',
      orientation: 'h',
      name: seriesName,
      y: barnDisplayOrder,
      customdata: barnOrder,
      x: barnOrder.map((barnName) => countsByBarnAndCrash[barnName][seriesName] || 0),
      hovertemplate: '<b>%{fullData.name}</b><br>%{customdata}<br>Crashes: %{x}<extra></extra>',
    }));
  }, [data]);

  const topCrashesChartData = useMemo(() => {
    if (!data?.fault) {
      return [];
    }

    const countsByCrash = {};
    data.fault.forEach((fault) => {
      const reportName = safeDecodeCrashReport(fault.details);
      countsByCrash[reportName] = (countsByCrash[reportName] || 0) + 1;
    });

    const topCrashes = Object.entries(countsByCrash)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    const crashOrder = topCrashes.map(([reportName]) => reportName).reverse();
    const crashDisplayOrder = crashOrder.map((reportName) => truncateAxisLabel(reportName));

    return [
      {
        type: 'bar',
        orientation: 'h',
        y: crashDisplayOrder,
        customdata: crashOrder,
        x: topCrashes.map(([, count]) => count).reverse(),
        hovertemplate: '<b>%{customdata}</b><br>Crashes: %{x}<extra></extra>',
      },
    ];
  }, [data]);

  const topOrgsChartData = useMemo(() => {
    if (!data?.fault) {
      return [];
    }

    const countsByOrg = {};
    data.fault.forEach((fault) => {
      const orgName = fault?.device?.device_assignments?.[0]?.feed_line?.farm?.organization?.name || 'Unknown org';
      countsByOrg[orgName] = (countsByOrg[orgName] || 0) + 1;
    });

    const topOrgs = Object.entries(countsByOrg)
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_ORGS_COUNT);
    const orgOrder = topOrgs.map(([orgName]) => orgName).reverse();
    const orgDisplayOrder = orgOrder.map((orgName) => truncateAxisLabel(orgName));

    return [
      {
        type: 'bar',
        orientation: 'h',
        y: orgDisplayOrder,
        customdata: orgOrder,
        x: topOrgs.map(([, count]) => count).reverse(),
        hovertemplate: '<b>%{customdata}</b><br>Crashes: %{x}<extra></extra>',
      },
    ];
  }, [data]);

  const tableHeaders = [
    { t: 'CRASH REPORT', s: 'l' },
    { t: 'OCCURRENCES', s: 'm' },
    { t: 'DEVICES AFFECTED', s: 'm' },
    { t: 'RECENTLY AT', s: 'm' },
  ];
  const orgTableHeaders = [
    { t: 'ORG', s: 'm' },
    { t: 'CRASHES', s: 'm' },
    { t: 'DEVICES AFFECTED', s: 'm' },
    { t: 'RECENTLY AT', s: 'm' },
  ];
  const deviceTableHeaders = [
    { t: 'DEVICE', s: 'm' },
    { t: 'ORG', s: 'm' },
    { t: 'BARN', s: 'm' },
    { t: 'FEED LINE', s: 'm' },
    { t: 'CRASHES', s: 'm' },
    { t: 'RECENTLY AT', s: 'm' },
  ];

  const topOrgRows = useMemo(() => {
    if (!data?.fault) {
      return [];
    }

    const countsByOrg = {};
    data.fault.forEach((fault) => {
      const orgName = fault?.device?.device_assignments?.[0]?.feed_line?.farm?.organization?.name || 'Unknown org';
      if (!countsByOrg[orgName]) {
        countsByOrg[orgName] = {
          crashes: 0,
          serials: new Set(),
          latestFaultId: fault?.id || null,
          latestFaultStartedAt: fault?.started_at || 0,
        };
      }

      countsByOrg[orgName].crashes += 1;
      if (fault?.device?.serial) {
        countsByOrg[orgName].serials.add(fault.device.serial);
      }
      if ((fault?.started_at || 0) > countsByOrg[orgName].latestFaultStartedAt) {
        countsByOrg[orgName].latestFaultStartedAt = fault.started_at;
        countsByOrg[orgName].latestFaultId = fault.id;
      }
    });

    return Object.entries(countsByOrg)
      .sort((a, b) => b[1].crashes - a[1].crashes)
      .slice(0, 25)
      .map(([orgName, orgData]) => {
        const serials = Array.from(orgData.serials).sort((a, b) => a.localeCompare(b));
        const params = new URLSearchParams();
        params.set('name', `${orgName} affected devices`);
        serials.forEach((serial) => params.append('d', serial));

        return [
          orgName,
          orgData.crashes,
          serials.length > 0 ? (
            <Link to={`/devices?${params.toString()}`}>{serials.length} device(s)</Link>
          ) : (
            '—'
          ),
          orgData.latestFaultId ? (
            <Link to={`/fault/${orgData.latestFaultId}`}>
              {dayjs.tz(1000 * orgData.latestFaultStartedAt).fromNow()}
            </Link>
          ) : (
            '—'
          ),
        ];
      });
  }, [data]);

  const topDeviceRows = useMemo(() => {
    if (!data?.fault) {
      return [];
    }

    const countsByDevice = {};
    data.fault.forEach((fault) => {
      const deviceId = fault?.device?.id || 'unknown-device';
      const serial = fault?.device?.serial || 'Unknown device';
      const assignment = fault?.device?.device_assignments?.[0];
      const orgName = assignment?.feed_line?.farm?.organization?.name || '—';
      const feedLineName = assignment?.feed_line?.name || '—';
      const farmName = assignment?.feed_line?.farm?.name || '—';

      if (!countsByDevice[deviceId]) {
        countsByDevice[deviceId] = {
          serial,
          orgName,
          farmName,
          feedLineName,
          crashes: 0,
          latestFaultId: fault?.id || null,
          latestFaultStartedAt: fault?.started_at || 0,
        };
      }
      countsByDevice[deviceId].crashes += 1;
      if ((fault?.started_at || 0) > countsByDevice[deviceId].latestFaultStartedAt) {
        countsByDevice[deviceId].latestFaultStartedAt = fault.started_at;
        countsByDevice[deviceId].latestFaultId = fault.id;
      }
    });

    return Object.values(countsByDevice)
      .sort((a, b) => b.crashes - a.crashes)
      .slice(0, 25)
      .map((entry) => [
        <Link to={`/devices?d=${encodeURIComponent(entry.serial)}&name=${encodeURIComponent(entry.serial)}`}>
          {entry.serial}
        </Link>,
        entry.orgName,
        entry.farmName,
        entry.feedLineName,
        entry.crashes,
        entry.latestFaultId ? (
          <Link to={`/fault/${entry.latestFaultId}`}>
            {dayjs.tz(1000 * entry.latestFaultStartedAt).fromNow()}
          </Link>
        ) : (
          '—'
        ),
      ]);
  }, [data]);

  // --- Function definitions ---
  function handleClickedOutside() {
    setShowDatePicker(false);
  }

  function onDateChange(e) {
    if (e.dateRange) {
      setDateRange({ from: e.dateRange.from, to: e.dateRange.to });
    }
    setShowDatePicker(false);
  }

  function onOrgFilterChange(selectedItems) {
    setSelectedOrgIds(selectedItems.map((item) => item.id));
  }

  // --- Render ---
  if (!privilegesLoading && !hasStaffAccess) {
    return (
      <Page className="CrashReportsMonitorPage" titleSegments={['Device Crash Monitor']}>
        <div className="CrashReportsMonitorPage-content">
          <div className="CrashReportsMonitorPage-header">
            <Heading level={3}>Access Denied</Heading>
          </div>
          <p>This page is only available to FeedFlo staff members.</p>
        </div>
      </Page>
    );
  }

  if (error) {
    console.error('Crash report monitor query error:', error);
    Sentry.captureException(error);
    toast.error('Error loading crash report monitor data');
    return (
      <Page className="CrashReportsMonitorPage" titleSegments={['Device Crash Monitor']}>
        <div className="CrashReportsMonitorPage-content">
          <div className="CrashReportsMonitorPage-header">
            <Heading level={3}>Error</Heading>
          </div>
          <p>Failed to load crash report data. Please try again.</p>
          <p>{error.message}</p>
        </div>
      </Page>
    );
  }

  const fromLabel = dateRange?.from?.format('MMM D') || '';
  const toLabel = dateRange?.to?.format('MMM D, YYYY') || '';
  const totalCrashes = data?.fault?.length || 0;
  const totalAffectedDevices = topDeviceRows.length;
  const totalAffectedOrgs = topOrgRows.length;

  return (
    <Page className="CrashReportsMonitorPage" titleSegments={['Device Crash Monitor']}>
      <div className="CrashReportsMonitorPage-content">
        <div className="CrashReportsMonitorPage-header">
          <div className="CrashReportsMonitorPage-headerTitleBlock">
            <Heading level={3}>Device Crash Monitor</Heading>
            <p className="CrashReportsMonitorPage-headerSubtitle">
              Live crash visibility by signature, org, barn, and device.
            </p>
          </div>
          <div className="CrashReportsMonitorPage-headerFilters">
            <div className="CrashReportsMonitorPage-branchFilter">
              <label className="CrashReportsMonitorPage-branchFilterLabel" htmlFor="firmwareBranchFilter">
                Firmware Branch ID (optional)
              </label>
              <input
                id="firmwareBranchFilter"
                className="CrashReportsMonitorPage-branchFilterInput"
                value={firmwareBranchId}
                onChange={(e) => setFirmwareBranchId(e.target.value)}
                placeholder="Leave blank for all branches"
              />
            </div>
            <div className="CrashReportsMonitorPage-orgFilter">
              <ComboboxMulti
                items={orgOptions}
                value={selectedOrgIds}
                onChange={onOrgFilterChange}
                emptyPlaceholder="Filter by organization..."
                label="Organizations"
                allowSelectAll
              />
            </div>
            <div className="CrashReportsMonitorPage-dateRangeFilter">
              <div
                className="CrashReportsMonitorPage-dateRangeButton"
                onClick={() => {
                  setShowDatePicker(!showDatePicker);
                }}
              >
                <div className="CrashReportsMonitorPage-dateRangeButtonIcon">
                  <CalendarIcon />
                </div>
                <div className="CrashReportsMonitorPage-dateRangeButtonText">{`${fromLabel} - ${toLabel}`}</div>
              </div>
              {showDatePicker && (
                <FeedFloDatePicker
                  onClickedOutside={() => handleClickedOutside()}
                  onChange={(e) => onDateChange(e)}
                  from={dateRange?.from?.toDate()}
                  to={dateRange?.to?.toDate()}
                />
              )}
            </div>
          </div>
        </div>

        <div className="CrashReportsMonitorPage-summaryRow">
          <div className="CrashReportsMonitorPage-summaryPill">{totalCrashes} crashes</div>
          <div className="CrashReportsMonitorPage-summaryPill">{totalAffectedOrgs} orgs affected</div>
          <div className="CrashReportsMonitorPage-summaryPill">{totalAffectedDevices} devices affected</div>
          <div className="CrashReportsMonitorPage-summaryPill">{`${fromLabel} - ${toLabel}`}</div>
        </div>

        <div className="CrashReportsMonitorPage-section">
          <Heading level={4}>Crash Timeline</Heading>
        </div>
        <div className="CrashReportsMonitorPage-chart">
          <Heading level={5}>Crash volume over time (fault {CRASH_REPORT_CODE})</Heading>
          <FeedFloLabChart
            loading={loading}
            data={chartData}
            layout={{
              margin: { t: 40, b: 110, l: 50, r: 20, autoexpand: false },
              barmode: 'stack',
              hovermode: 'x unified',
              hoverlabel: {
                bgcolor: 'rgba(15, 23, 42, 0.78)',
                bordercolor: 'rgba(15, 23, 42, 0.9)',
                font: { color: '#f8fafc' },
                namelength: -1,
              },
              legend: {
                x: 1,
                y: 0,
                xanchor: 'right',
                yanchor: 'bottom',
                bgcolor: 'rgba(15, 23, 42, 0.45)',
                bordercolor: 'rgba(15, 23, 42, 0.6)',
                font: { color: '#f8fafc' },
              },
              xaxis: {
                title: { text: 'Time (hour bins)', standoff: 20 },
                tickangle: -30,
                automargin: true,
              },
              yaxis: { title: 'Crash report count' },
            }}
          />
        </div>

        <div className="CrashReportsMonitorPage-section">
          <Heading level={4}>Impact Breakdown</Heading>
        </div>
        <div className="CrashReportsMonitorPage-topCharts">
          <div className="CrashReportsMonitorPage-chart CrashReportsMonitorPage-chart--half">
            <Heading level={5}>Most common crash signatures</Heading>
            <FeedFloLabChart
              loading={loading}
              data={topCrashesChartData}
              layout={{
                margin: { t: 40, b: 60, l: 150, r: 20, autoexpand: false },
                hoverlabel: {
                  bgcolor: 'rgba(15, 23, 42, 0.78)',
                  bordercolor: 'rgba(15, 23, 42, 0.9)',
                  font: { color: '#f8fafc' },
                  namelength: -1,
                },
                xaxis: { title: 'Crash report count', automargin: true },
                yaxis: { automargin: true },
              }}
            />
          </div>

          <div className="CrashReportsMonitorPage-chart CrashReportsMonitorPage-chart--half">
            <Heading level={5}>Most affected organizations</Heading>
            <FeedFloLabChart
              loading={loading}
              data={topOrgsChartData}
              layout={{
                margin: { t: 40, b: 60, l: 150, r: 20, autoexpand: false },
                hoverlabel: {
                  bgcolor: 'rgba(15, 23, 42, 0.78)',
                  bordercolor: 'rgba(15, 23, 42, 0.9)',
                  font: { color: '#f8fafc' },
                  namelength: -1,
                },
                xaxis: { title: 'Crash report count', automargin: true },
                yaxis: { automargin: true },
              }}
            />
          </div>

          <div className="CrashReportsMonitorPage-chart CrashReportsMonitorPage-chart--half">
            <Heading level={5}>Most affected barns</Heading>
            <FeedFloLabChart
              loading={loading}
              data={topBarnsChartData}
              layout={{
                margin: { t: 40, b: 60, l: 150, r: 20, autoexpand: false },
                barmode: 'stack',
                showlegend: false,
                hoverlabel: {
                  bgcolor: 'rgba(15, 23, 42, 0.78)',
                  bordercolor: 'rgba(15, 23, 42, 0.9)',
                  font: { color: '#f8fafc' },
                  namelength: -1,
                },
                xaxis: { title: 'Crash report count', automargin: true },
                yaxis: { automargin: true },
              }}
            />
          </div>
        </div>

        <div className="CrashReportsMonitorPage-table">
          <Heading level={4}>Crash Signatures</Heading>
          <FeedFloTable
            className="CrashReportsMonitorPage-crashTable"
            headers={tableHeaders}
            rows={crashReportRows}
            loading={loading}
            itemTotalCount={crashReportRows.length}
            maxRows={crashReportRows.length || 1}
          />
        </div>

        <div className="CrashReportsMonitorPage-table">
          <Heading level={4}>Organizations Most Affected</Heading>
          <FeedFloTable
            className="CrashReportsMonitorPage-orgTable"
            headers={orgTableHeaders}
            rows={topOrgRows}
            loading={loading}
            itemTotalCount={topOrgRows.length}
            maxRows={topOrgRows.length || 1}
          />
        </div>

        <div className="CrashReportsMonitorPage-table">
          <Heading level={4}>Devices Most Affected</Heading>
          <FeedFloTable
            className="CrashReportsMonitorPage-deviceTable"
            headers={deviceTableHeaders}
            rows={topDeviceRows}
            loading={loading}
            itemTotalCount={topDeviceRows.length}
            maxRows={topDeviceRows.length || 1}
          />
        </div>
      </div>
    </Page>
  );
}

export default CrashReportsMonitorPage;

